import fp from 'lodash/fp';
import React, { useState, useCallback, useRef, useLayoutEffect, FC, ComponentProps, ChangeEventHandler } from 'react';

import { Text } from 'Modules/ui';
import { useAction } from 'Modules/core/redux';
import { useTranslation } from 'Modules/core/i18n';
import { IPaginationParameters, paginationActions, usePagination } from 'Modules/business/data';

import styles from './index.module.scss';


type ListFilterFieldProps = {
    path: string;
    field: string;
}

export const ListFilterField: FC<ListFilterFieldProps> = React.memo(({ path, field }) => {
    const parameters = usePagination(path, 'parameters');
    const [filter, changeFilterValue] = useState(() => fp.getOr('', `filters[${field}]`, parameters));
    const setParameters = useAction<string, IPaginationParameters>(
        value => paginationActions.setParametersThrottled(path, {
            ...parameters,
            filters: {
                ...parameters.filters,
                [field]: value,
            },
        }),
        [path, field, parameters],
    );

    const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        ({ target: { value } }) => {
            changeFilterValue(value);
            setParameters(value);
        },
        [setParameters],
    );

    const queue = usePagination(path, 'queue');
    const ref = useRef<HTMLInputElement>();
    useLayoutEffect(
        () => {
            ref.current.blur();
            ref.current.focus();
        },
        [queue],
    );

    const { t } = useTranslation('listFilterField');
    return (
        <Text<ComponentProps<'input'>>
            componentRef={ref}
            component='input'
            className={styles.field}

            value={filter}
            onChange={onInputChange}

            autoFocus
            placeholder={t('placeholder') as string}
        />
    );
});
